import { Card, Stack, Group, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { useT } from '../../../i18n/I18nProvider';
import type { Doc } from '../spec';
import { CodeBlock } from './CodeBlock';

/**
 * What the platform sends you, as opposed to what you ask it for.
 *
 * Easy to leave out of an API document and impossible to integrate without: a
 * developer building against this platform needs to know which events arrive,
 * what the headers mean and how to prove a delivery is ours. The event list is
 * read from the document's OpenAPI 3.1 `webhooks` section, which is where the
 * gateway publishes it.
 */
export function WebhookEvents({ doc }: { doc: Doc | undefined }) {
  const t = useT();
  const events = Object.entries(doc?.webhooks ?? {}).map(([name, item]) => ({
    name,
    summary: item?.post?.summary ?? '',
    description: item?.post?.description ?? ''
  }));

  const verify = `import crypto from 'node:crypto';

// The raw body, exactly as it arrived. Parsing and re-serialising it changes
// the bytes and the signature will not match.
export function isOurs(rawBody, headers, secret) {
  const timestamp = headers['x-repletoryreach-timestamp'];
  const signature = headers['x-repletoryreach-signature'];
  if (!timestamp || !signature) return false;

  // Refuse anything old enough to be a replay of a delivery you already took.
  const age = Math.abs(Date.now() / 1000 - Number(timestamp));
  if (!Number.isFinite(age) || age > 300) return false;

  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${timestamp}.\${rawBody}\`)
    .digest('hex');

  const given = signature.replace(/^sha256=/, '');
  // Constant time: a plain === leaks the answer one character at a time.
  return given.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(given), Buffer.from(expected));
}`;

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.hooks.title')}</Title>
          <Text role="body">{t('developers.hooks.body')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('developers.hooks.registerNote')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.hooks.events')}</Title>
          {events.length === 0 ? (
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('developers.hooks.noEvents')}
            </Text>
          ) : (
            <Stack gap="sm">
              {events.map((event) => (
                <Group key={event.name} gap="sm" align="flex-start" wrap="nowrap">
                  <Badge variant="light" className="rr-dev__nowrap-badge rr-dev__identifier">
                    {event.name}
                  </Badge>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {event.summary}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.hooks.headers')}</Title>
          <div className="rr-dev__table-wrap">
            <table className="rr-dev__table">
              <thead>
                <tr>
                  <th scope="col">{t('developers.hooks.header')}</th>
                  <th scope="col">{t('developers.schema.notes')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>X-RepletoryReach-Event</code></td>
                  <td><Text role="caption">{t('developers.hooks.hEvent')}</Text></td>
                </tr>
                <tr>
                  <td><code>X-RepletoryReach-Delivery-Id</code></td>
                  <td><Text role="caption">{t('developers.hooks.hDelivery')}</Text></td>
                </tr>
                <tr>
                  <td><code>X-RepletoryReach-Timestamp</code></td>
                  <td><Text role="caption">{t('developers.hooks.hTimestamp')}</Text></td>
                </tr>
                <tr>
                  <td><code>X-RepletoryReach-Signature</code></td>
                  <td><Text role="caption">{t('developers.hooks.hSignature')}</Text></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.hooks.verify')}</Title>
          <Text role="body">{t('developers.hooks.verifyBody')}</Text>
          <CodeBlock code={verify} label={t('developers.hooks.verify')} />
          <Text role="body">{t('developers.hooks.retries')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}
